import type {
  JourneyTimingDependencyClass,
  JourneyTimingInteractionName,
  JourneyTimingObserver,
  JourneyTimingOutcome,
  JourneyTimingRenderedState,
} from '@adapters/telemetry/JourneyTimingObserver';
import { R4PreTriageApplyTaskBuilder } from '@adapters/fhir/R4PreTriageApplyTaskBuilder';
import { R4PreTriageOutcomeParser } from '@adapters/fhir/R4PreTriageOutcomeParser';
import { R4TriageQuestionnaireParser } from '@adapters/fhir/R4TriageQuestionnaireParser';
import { R4QuestionnaireResponseBuilder } from '@adapters/fhir/R4QuestionnaireResponseBuilder';
import {
  type PreTriageLoadResultViewModel,
  type PreTriageSubmissionResultViewModel,
} from '@adapters/presenters/PreTriagePresenter';
import { PreTriagePresenter } from '@adapters/presenters/PreTriagePresenter';
import type { AppEntryContext } from '@core/entities/AppEntryContext';
import type { TriageAnswerRecord, TriageAnswerValue, TriageChoiceOption, TriageQuestion, TriageQuestionnaire } from '@core/entities/Triage';
import { GuidanceResponseStatusEnum } from '@core/fhir-r4/fhir';
import { PreTriageGatewayError } from '@core/ports/PreTriageGateway';
import { ApplyPreTriageTaskUseCase } from '@core/usecases/ApplyPreTriageTaskUseCase';
import { DeterminePreTriageSubmissionTransitionUseCase } from '@core/usecases/DeterminePreTriageSubmissionTransitionUseCase';
import { GetAppEntryContextUseCase } from '@core/usecases/GetAppEntryContextUseCase';
import { GetTriageDeferredOptionsUseCase } from '@core/usecases/GetTriageDeferredOptionsUseCase';
import { PreparePreTriageSubmissionAnswersUseCase } from '@core/usecases/PreparePreTriageSubmissionAnswersUseCase';
import { RetryBudgetUseCase } from '@core/usecases/RetryBudgetUseCase';
import { UnresolvedSubmissionUseCase } from '@core/usecases/UnresolvedSubmissionUseCase';
import { JourneyDraftStore } from '@infrastructure/storage/JourneyDraftStore';

type PendingJourneyTiming = Readonly<{
  interactionName: JourneyTimingInteractionName;
  outcome: JourneyTimingOutcome;
  dependencyClass: JourneyTimingDependencyClass;
  renderedState: JourneyTimingRenderedState;
  backendJoinKey?: string;
  startedAtMs: number;
}>;

export class PreTriageController {
  private static readonly routePath = '/journey-start';
  private static readonly internalDependencyClass: JourneyTimingDependencyClass = 'internal';
  private static readonly providerBackedDependencyClass: JourneyTimingDependencyClass = 'provider-backed';

  private questionnaire?: TriageQuestionnaire;
  private failedLoad = false;
  private appEntryContextPromise?: Promise<AppEntryContext>;
  private activeStepId?: string;
  private pendingLoadTiming?: PendingJourneyTiming;
  private pendingSubmissionTiming?: PendingJourneyTiming;
  private pendingSubmissionCompleteTiming?: PendingJourneyTiming;
  private pendingOutcomeRenderTiming?: PendingJourneyTiming;

  public constructor(
    private readonly applyPreTriageTaskUseCase: ApplyPreTriageTaskUseCase,
    private readonly questionnaireParser: R4TriageQuestionnaireParser,
    private readonly questionnaireResponseBuilder: R4QuestionnaireResponseBuilder,
    private readonly applyTaskBuilder: R4PreTriageApplyTaskBuilder,
    private readonly outcomeParser: R4PreTriageOutcomeParser,
    private readonly preTriagePresenter: PreTriagePresenter,
    private readonly journeyDraftStore: JourneyDraftStore,
    private readonly getAppEntryContextUseCase: GetAppEntryContextUseCase,
    private readonly preparePreTriageSubmissionAnswersUseCase: PreparePreTriageSubmissionAnswersUseCase,
    private readonly determinePreTriageSubmissionTransitionUseCase: DeterminePreTriageSubmissionTransitionUseCase,
    private readonly retryBudgetUseCase: RetryBudgetUseCase,
    private readonly unresolvedSubmissionUseCase: UnresolvedSubmissionUseCase,
    private readonly getTriageDeferredOptionsUseCase?: GetTriageDeferredOptionsUseCase,
    private readonly journeyTimingObserver?: JourneyTimingObserver,
    private readonly nowProvider: () => number = () => globalThis.performance.now(),
  ) {}

  public async load(): Promise<PreTriageLoadResultViewModel> {
    return this.loadOnce();
  }

  public async submit(answers: TriageAnswerRecord): Promise<PreTriageSubmissionResultViewModel> {
    return this.submitOnce(answers);
  }

  public async retry(): Promise<PreTriageLoadResultViewModel> {
    if (!this.failedLoad || !this.retryBudgetUseCase.isRetryAvailable()) {
      throw new Error('There is no failed step to retry.');
    }

    this.retryBudgetUseCase.consumeRetry();
    return this.load();
  }

  private async loadOnce(): Promise<PreTriageLoadResultViewModel> {
    this.pendingLoadTiming = this.startInteraction('journey_start.load');

    const restoredStep = this.journeyDraftStore.getActiveLiveTriageStep();

    if (restoredStep) {
      this.pendingLoadTiming = this.withDependencyClass(this.pendingLoadTiming, PreTriageController.internalDependencyClass);
      this.questionnaire = restoredStep.questionnaire;
      this.activeStepId = restoredStep.stepId;
      this.pendingLoadTiming = this.withOutcome(this.pendingLoadTiming, 'success', 'questionnaire');
      return this.preTriagePresenter.presentQuestionnaire(this.questionnaire);
    }

    if (!restoredStep && this.journeyDraftStore.hasLiveTriageState()) {
      this.journeyDraftStore.markCorruptedState();
    }

    try {
      const gatewayResult = await this.applyPreTriageTaskUseCase.execute(this.applyTaskBuilder.buildInitial());
      const { guidanceResponse } = gatewayResult;
      this.pendingLoadTiming = this.withBackendJoinKey(this.pendingLoadTiming, gatewayResult.backendJoinKey);

      if (guidanceResponse.status === GuidanceResponseStatusEnum.SUCCESS) {
        const outcomeResult = this.preTriagePresenter.presentLoadOutcome(this.outcomeParser.parse(gatewayResult));
        this.pendingLoadTiming = this.withOutcome(this.pendingLoadTiming, 'success', 'outcome');
        this.pendingOutcomeRenderTiming = this.startInteraction(
          'triage.result.render',
          'outcome',
          PreTriageController.internalDependencyClass,
          gatewayResult.backendJoinKey,
        );
        this.completeSuccessfulStep();
        return outcomeResult;
      }

      this.questionnaire = this.questionnaireParser.parse(guidanceResponse);
      this.activeStepId = this.getStepId(this.questionnaire);
      this.journeyDraftStore.initializeLiveTriageStep(this.activeStepId, this.questionnaire);
      this.pendingLoadTiming = this.withOutcome(this.pendingLoadTiming, 'success', 'questionnaire');
      const result = this.preTriagePresenter.presentQuestionnaire(this.questionnaire);
      this.completeSuccessfulStep();
      return result;
    } catch (error) {
      this.pendingLoadTiming = this.withOutcome(this.pendingLoadTiming, this.classifyErrorOutcome(error), 'failure');
      return this.preTriagePresenter.presentFailure(error, this.rememberLoadFailure());
    }
  }

  public notifyRenderedState(renderedState: JourneyTimingRenderedState): void {
    const completedAtMs = this.nowProvider();

    this.pendingLoadTiming = this.completePendingInteraction(this.pendingLoadTiming, renderedState, completedAtMs);
    this.pendingSubmissionTiming = this.completePendingInteraction(this.pendingSubmissionTiming, renderedState, completedAtMs);
    this.pendingSubmissionCompleteTiming = this.completePendingInteraction(this.pendingSubmissionCompleteTiming, renderedState, completedAtMs);
    this.pendingOutcomeRenderTiming = this.completePendingInteraction(this.pendingOutcomeRenderTiming, renderedState, completedAtMs);
  }

  public getCurrentAnswers(): TriageAnswerRecord {
    if (!this.activeStepId) {
      return {};
    }

    return this.journeyDraftStore.getLiveTriageAnswers(this.activeStepId);
  }

  private async submitOnce(answers: TriageAnswerRecord): Promise<PreTriageSubmissionResultViewModel> {
    if (this.unresolvedSubmissionUseCase.hasUnknownOutcome()) {
      throw new Error('Restart the triage journey before submitting answers again.');
    }

    if (!this.questionnaire || !this.activeStepId) {
      throw new Error('Reload the triage start page before submitting answers.');
    }

    const submittedQuestionnaire = this.questionnaire;
    const submittedWorkflowStepIdentity = submittedQuestionnaire.workflowStepIdentity;
    if (!submittedWorkflowStepIdentity) {
      throw new Error('Reload the triage start page before submitting answers.');
    }

    const submittedStepId = this.activeStepId;
    const submittedJourneyRevision = submittedWorkflowStepIdentity.journeyRevision;
    const preparedAnswers = this.preparePreTriageSubmissionAnswersUseCase.execute({
      questionnaire: submittedQuestionnaire,
      answers,
    });
    this.journeyDraftStore.recordLiveTriageAnswers(submittedStepId, preparedAnswers.normalizedAnswers);
    this.pendingSubmissionTiming = this.startInteraction('triage.question.submit');
    this.pendingSubmissionCompleteTiming = undefined;
    let submissionAttempted = false;

    try {
      const questionnaireResponse = this.questionnaireResponseBuilder.build(
        submittedQuestionnaire,
        preparedAnswers.submissionAnswers,
      );
      const appEntryContext = await this.getAppEntryContext();
      const taskPayload = this.applyTaskBuilder.buildContinuation(
        submittedQuestionnaire.encounterId,
        questionnaireResponse,
        submittedWorkflowStepIdentity,
        {
          patientId: appEntryContext.proxyContext.patientIdentifier ?? undefined,
          birthDate: appEntryContext.patientBirthDate ?? undefined,
          gender: preparedAnswers.patientSex,
        },
      );
      submissionAttempted = true;
      const gatewayResult = await this.applyPreTriageTaskUseCase.execute(taskPayload);
      const { guidanceResponse } = gatewayResult;
      this.pendingSubmissionTiming = this.withBackendJoinKey(this.pendingSubmissionTiming, gatewayResult.backendJoinKey);

      if (guidanceResponse.status === GuidanceResponseStatusEnum.DATA_REQUIRED || guidanceResponse.status === GuidanceResponseStatusEnum.DATA_REQUESTED) {
        const parsedQuestionnaire = this.questionnaireParser.parse(guidanceResponse);
        const nextStepId = this.getStepId(parsedQuestionnaire);
        const transition = this.determinePreTriageSubmissionTransitionUseCase.execute({
          submittedStepId,
          activeStepId: this.activeStepId,
          submittedJourneyRevision,
          nextJourneyRevision: parsedQuestionnaire.workflowStepIdentity?.journeyRevision ?? submittedJourneyRevision,
          isStoredNextStep: this.journeyDraftStore.hasLiveTriageStep(nextStepId),
          wouldAdvanceCreateCycle: this.journeyDraftStore.wouldAdvanceCreateCycle(submittedStepId, nextStepId),
        });

        if (transition === 'ignore-stale-response') {
          this.pendingSubmissionTiming = this.withOutcome(this.pendingSubmissionTiming, 'success', 'questionnaire');
          if (!this.questionnaire) {
            throw new Error('Reload the triage start page before submitting answers.');
          }

          this.completeSuccessfulStep();
          return this.preTriagePresenter.presentNextQuestionnaire(this.questionnaire);
        }

        this.questionnaire = parsedQuestionnaire;
        this.activeStepId = this.getStepId(this.questionnaire);
        switch (transition) {
          case 'advance-after-revision-change':
            this.journeyDraftStore.advanceLiveTriageStepAfterRevisionChange(submittedStepId, this.activeStepId, this.questionnaire);
            break;
          case 'initialize-live-triage-step':
            this.journeyDraftStore.initializeLiveTriageStep(this.activeStepId, this.questionnaire);
            break;
          default:
            this.questionnaire = this.journeyDraftStore.advanceLiveTriageStep(submittedStepId, this.activeStepId, this.questionnaire);
            break;
        }

        this.pendingSubmissionTiming = this.withOutcome(this.pendingSubmissionTiming, 'success', 'questionnaire');
        const result = this.preTriagePresenter.presentNextQuestionnaire(this.questionnaire);
        this.completeSuccessfulStep();
        return result;
      }

      const outcomeResult = this.preTriagePresenter.presentSubmissionOutcome(this.outcomeParser.parse(gatewayResult));
      this.pendingSubmissionTiming = this.withOutcome(this.pendingSubmissionTiming, 'success', 'outcome');
      this.pendingSubmissionCompleteTiming = this.withInteractionName(this.pendingSubmissionTiming, 'triage.submission.complete');
      this.pendingOutcomeRenderTiming = this.startInteraction(
        'triage.result.render',
        'outcome',
        PreTriageController.internalDependencyClass,
        gatewayResult.backendJoinKey,
      );
      this.completeSuccessfulStep();
      return outcomeResult;
    } catch (error) {
      this.pendingSubmissionTiming = this.withOutcome(this.pendingSubmissionTiming, this.classifyErrorOutcome(error), 'failure');
      if (submissionAttempted) {
        this.questionnaire = undefined;
        this.activeStepId = undefined;
        this.unresolvedSubmissionUseCase.markUnknownOutcome();
      }
      return this.preTriagePresenter.presentSubmissionFailure(error, false);
    }
  }

  private rememberLoadFailure(): boolean {
    this.failedLoad = true;
    return this.retryBudgetUseCase.isRetryAvailable();
  }

  private completeSuccessfulStep(): void {
    this.retryBudgetUseCase.restoreRetry();
    this.unresolvedSubmissionUseCase.clearUnknownOutcome();
    this.failedLoad = false;
  }

  public async loadDeferredOptions(linkId: string, query?: string): Promise<readonly TriageChoiceOption[]> {
    if (!this.questionnaire) {
      throw new Error('Reload the triage start page before loading deferred options.');
    }

    const question = this.findQuestion(this.questionnaire.questions, linkId);
    if (!question) {
      throw new Error(`Question '${linkId}' was not found in the active triage questionnaire.`);
    }

    const sourceListUrl = question.metadata?.sourceListUrl;
    if (!sourceListUrl || !this.isDeferredSourceListUrl(sourceListUrl) || !this.getTriageDeferredOptionsUseCase) {
      return question.options ?? [];
    }

    const normalizedQuery = query?.trim();
    if (!normalizedQuery) {
      this.questionnaire = this.updateQuestionnaireOptions(this.questionnaire, linkId, [], false);
      this.clearStoredAnswer(linkId);
      this.persistActiveQuestionnaire();
      return [];
    }

    const result = await this.getTriageDeferredOptionsUseCase.execute(sourceListUrl, normalizedQuery);
    // Re-read after the await so overlapping searches accumulate rather than overwrite each other.
    this.questionnaire = this.updateQuestionnaireOptions(this.questionnaire, linkId, result.items);
    this.persistActiveQuestionnaire();
    return this.findQuestion(this.questionnaire.questions, linkId)?.options ?? [];
  }

  private findQuestion(questions: readonly TriageQuestion[], linkId: string): TriageQuestion | undefined {
    for (const question of questions) {
      if (question.linkId === linkId) {
        return question;
      }

      const childMatch = question.children ? this.findQuestion(question.children, linkId) : undefined;
      if (childMatch) {
        return childMatch;
      }
    }

    return undefined;
  }

  private updateQuestionnaireOptions(
    questionnaire: TriageQuestionnaire,
    linkId: string,
    options: readonly TriageChoiceOption[],
    mergeOptions = true,
  ): TriageQuestionnaire {
    const updatedQuestions = questionnaire.questions.map((question) => this.updateQuestionOptions(question, linkId, options, mergeOptions));

    return {
      ...questionnaire,
      questions: updatedQuestions,
    };
  }

  private updateQuestionOptions(
    question: TriageQuestion,
    linkId: string,
    options: readonly TriageChoiceOption[],
    mergeOptions: boolean,
  ): TriageQuestion {
    if (question.linkId === linkId) {
      return {
        ...question,
        options: mergeOptions ? this.mergeQuestionOptions(question.options ?? [], options) : options,
      };
    }

    if (!question.children) {
      return question;
    }

    return {
      ...question,
      children: question.children.map((child) => this.updateQuestionOptions(child, linkId, options, mergeOptions)),
    };
  }

  private mergeQuestionOptions(
    existingOptions: readonly TriageChoiceOption[],
    newOptions: readonly TriageChoiceOption[],
  ): readonly TriageChoiceOption[] {
    const optionsByCode = new Map(existingOptions.map((option) => [option.code, option]));
    for (const option of newOptions) {
      optionsByCode.set(option.code, option);
    }

    return [...optionsByCode.values()];
  }

  private isDeferredSourceListUrl(sourceListUrl: string): boolean {
    return sourceListUrl.startsWith('/api/triage/');
  }

  private persistActiveQuestionnaire(): void {
    if (!this.questionnaire || !this.activeStepId) {
      return;
    }

    this.journeyDraftStore.advanceLiveTriageStep(this.activeStepId, this.activeStepId, this.questionnaire);
  }

  private startInteraction(
    interactionName: JourneyTimingInteractionName,
    renderedState: JourneyTimingRenderedState = 'questionnaire',
    dependencyClass: JourneyTimingDependencyClass = PreTriageController.providerBackedDependencyClass,
    backendJoinKey?: string,
  ): PendingJourneyTiming {
    return {
      interactionName,
      outcome: 'success',
      dependencyClass,
      renderedState,
      backendJoinKey,
      startedAtMs: this.nowProvider(),
    };
  }

  private withDependencyClass(
    pendingInteraction: PendingJourneyTiming | undefined,
    dependencyClass: JourneyTimingDependencyClass,
  ): PendingJourneyTiming | undefined {
    if (!pendingInteraction) {
      return undefined;
    }

    return {
      ...pendingInteraction,
      dependencyClass,
    };
  }

  private withInteractionName(
    pendingInteraction: PendingJourneyTiming | undefined,
    interactionName: JourneyTimingInteractionName,
  ): PendingJourneyTiming | undefined {
    if (!pendingInteraction) {
      return undefined;
    }

    return {
      ...pendingInteraction,
      interactionName,
    };
  }

  private withBackendJoinKey(
    pendingInteraction: PendingJourneyTiming | undefined,
    backendJoinKey: string | undefined,
  ): PendingJourneyTiming | undefined {
    if (!pendingInteraction || !backendJoinKey) {
      return pendingInteraction;
    }

    return {
      ...pendingInteraction,
      backendJoinKey,
    };
  }

  private withOutcome(
    pendingInteraction: PendingJourneyTiming | undefined,
    outcome: JourneyTimingOutcome,
    renderedState: JourneyTimingRenderedState,
  ): PendingJourneyTiming | undefined {
    if (!pendingInteraction) {
      return undefined;
    }

    return {
      ...pendingInteraction,
      outcome,
      renderedState,
    };
  }

  private completePendingInteraction(
    pendingInteraction: PendingJourneyTiming | undefined,
    renderedState: JourneyTimingRenderedState,
    completedAtMs: number,
  ): PendingJourneyTiming | undefined {
    if (pendingInteraction?.renderedState === renderedState) {
      this.journeyTimingObserver?.record({
        interactionName: pendingInteraction.interactionName,
        outcome: pendingInteraction.outcome,
        dependencyClass: pendingInteraction.dependencyClass,
        routePath: PreTriageController.routePath,
        durationMs: Math.max(0, completedAtMs - pendingInteraction.startedAtMs),
        correlationSource: 'server-trace',
        backendJoinKey: pendingInteraction.backendJoinKey,
        renderedState,
      });

      return undefined;
    }

    return pendingInteraction;
  }

  private classifyErrorOutcome(error: unknown): JourneyTimingOutcome {
    if (error instanceof PreTriageGatewayError && error.code === 'session-required') {
      return 'blocked';
    }

    if (error instanceof PreTriageGatewayError && (error.code === 'offline' || error.code === 'retry-exhausted')) {
      return 'retryable-failure';
    }

    return 'terminal-failure';
  }

  private clearStoredAnswer(linkId: string): void {
    if (!this.activeStepId) {
      return;
    }

    const storedAnswers = this.journeyDraftStore.getLiveTriageAnswers(this.activeStepId);
    if (!(linkId in storedAnswers)) {
      return;
    }

    const updatedAnswers: Record<string, TriageAnswerValue> = { ...storedAnswers };
    delete updatedAnswers[linkId];
    this.journeyDraftStore.recordLiveTriageAnswers(this.activeStepId, updatedAnswers);
  }

  private getAppEntryContext(): Promise<AppEntryContext> {
    let appEntryContextPromise = this.appEntryContextPromise;
    if (!appEntryContextPromise) {
      appEntryContextPromise = this.getAppEntryContextUseCase.execute()
        .catch((error) => {
          this.appEntryContextPromise = undefined;
          throw error;
        });
      this.appEntryContextPromise = appEntryContextPromise;
    }

    return appEntryContextPromise;
  }

  private getStepId(questionnaire: TriageQuestionnaire): string {
    if (!questionnaire.workflowStepIdentity) {
      throw new Error('The triage questionnaire did not include workflow step identity.');
    }

    const { journeyRevision, stepSequence, questionnaireEventSequence } = questionnaire.workflowStepIdentity;
    // questionnaireEventSequence must be included: journeyRevision/stepSequence can restart
    // (e.g. the pre-triage → main-triage boundary), and without it the step key collides
    // with an earlier visit, forcing a destructive back-link chain reset.
    return `${questionnaire.encounterId}:${journeyRevision}:${stepSequence}:${questionnaireEventSequence}`;
  }
}
